import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tab } from '@headlessui/react';
import { BugAntIcon, LightBulbIcon, CameraIcon } from '@heroicons/react/24/outline';
import { supabase } from '@/services/supabase';
import { Card, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Select from '@/components/common/Select';
import EmptyState from '@/components/common/EmptyState';
import { formatTimeAgo } from '@/utils/formatters';
import toast from 'react-hot-toast';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface FeedbackItem {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  type: 'bug' | 'idea';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  upvote_count: number;
  admin_response: string | null;
  admin_responded_at: string | null;
  screenshot_url: string | null;
  user_type: 'consumer' | 'business';
  app_version: string | null;
  created_at: string;
  updated_at: string;
}

const statusColors: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

async function fetchFeedback(type: string) {
  const { data, error } = await supabase
    .from('feedback_items')
    .select('*')
    .eq('type', type)
    .order('upvote_count', { ascending: false });

  if (error) throw error;
  return data as FeedbackItem[];
}

async function updateFeedbackStatus(id: string, status: string) {
  const { error } = await supabase
    .from('feedback_items')
    .update({ status })
    .eq('id', id);

  if (error) throw error;
}

async function submitAdminResponse(id: string, response: string) {
  const { error } = await supabase
    .from('feedback_items')
    .update({
      admin_response: response,
      admin_responded_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) throw error;
}

export default function FeedbackManagement() {
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedItem, setSelectedItem] = useState<FeedbackItem | null>(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: bugs, isLoading: bugsLoading } = useQuery({
    queryKey: ['feedback', 'bug'],
    queryFn: () => fetchFeedback('bug'),
  });

  const { data: ideas, isLoading: ideasLoading } = useQuery({
    queryKey: ['feedback', 'idea'],
    queryFn: () => fetchFeedback('idea'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateFeedbackStatus(id, status),
    onSuccess: () => {
      toast.success('Status updated');
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: () => {
      toast.error('Failed to update status');
    },
  });

  const responseMutation = useMutation({
    mutationFn: () => submitAdminResponse(selectedItem!.id, adminResponse),
    onSuccess: () => {
      toast.success('Response saved');
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      setSelectedItem(null);
      setAdminResponse('');
    },
    onError: () => {
      toast.error('Failed to save response');
    },
  });

  const tabs = [
    { name: 'Bug Reports', icon: BugAntIcon, count: bugs?.length || 0 },
    { name: 'Feature Ideas', icon: LightBulbIcon, count: ideas?.length || 0 },
  ];

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  const filterOptions = [
    { value: 'all', label: 'All Statuses' },
    ...statusOptions,
  ];

  const filterItems = (items: FeedbackItem[] | undefined) => {
    if (!items) return [];
    if (statusFilter === 'all') return items;
    return items.filter(i => i.status === statusFilter);
  };

  const renderTable = (items: FeedbackItem[] | undefined, loading: boolean) => {
    const filtered = filterItems(items);

    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (!filtered || filtered.length === 0) {
      return (
        <EmptyState
          title="No feedback items"
          description="No feedback has been submitted yet."
        />
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Votes
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Version
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="max-w-md">
                    <div className="flex items-center gap-1.5">
                      <span className={classNames(
                        'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase flex-shrink-0',
                        item.user_type === 'business' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                      )}>
                        {item.user_type === 'business' ? 'BIZ' : 'CON'}
                      </span>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.title}
                      </p>
                      {item.screenshot_url && (
                        <CameraIcon className="w-4 h-4 text-gray-400 flex-shrink-0" title="Has screenshot" />
                      )}
                    </div>
                    {item.description && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {item.description}
                      </p>
                    )}
                    {item.admin_response && (
                      <p className="text-xs text-brand mt-1 truncate">
                        Admin responded
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-700">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    {item.upvote_count}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <select
                    value={item.status}
                    onChange={(e) =>
                      statusMutation.mutate({ id: item.id, status: e.target.value })
                    }
                    className={classNames(
                      'text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer',
                      statusColors[item.status]
                    )}
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-4 text-sm text-gray-500">
                  {item.app_version ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      v{item.app_version}
                    </span>
                  ) : (
                    <span className="text-gray-300">&mdash;</span>
                  )}
                </td>
                <td className="px-4 py-4 text-sm text-gray-500">
                  {formatTimeAgo(item.created_at)}
                </td>
                <td className="px-4 py-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedItem(item);
                      setAdminResponse(item.admin_response || '');
                    }}
                  >
                    Respond
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Feedback Management</h1>
        <p className="mt-1 text-sm text-gray-500">
          Review and respond to user bug reports and feature ideas
        </p>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700">Filter:</span>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={filterOptions}
          aria-label="Filter feedback items"
        />
      </div>

      {/* Tabs */}
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-brand shadow'
                    : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
                )
              }
            >
              <div className="flex items-center justify-center gap-2">
                <tab.icon className="w-5 h-5" />
                {tab.name}
                <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">
                  {tab.count}
                </span>
              </div>
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels className="mt-4">
          <Tab.Panel>
            <Card>
              <CardBody>{renderTable(bugs, bugsLoading)}</CardBody>
            </Card>
          </Tab.Panel>
          <Tab.Panel>
            <Card>
              <CardBody>{renderTable(ideas, ideasLoading)}</CardBody>
            </Card>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Respond Modal */}
      <Modal
        isOpen={!!selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setAdminResponse('');
        }}
        title={selectedItem ? `Respond to: ${selectedItem.title}` : ''}
      >
        {selectedItem && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className={classNames(
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                selectedItem.user_type === 'business' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
              )}>
                {selectedItem.user_type === 'business' ? 'Business' : 'Consumer'}
              </span>
              {selectedItem.app_version && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                  v{selectedItem.app_version}
                </span>
              )}
            </div>

            {selectedItem.description && (
              <div>
                <p className="block text-sm font-medium text-gray-700 mb-1">
                  User Description
                </p>
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  {selectedItem.description}
                </p>
              </div>
            )}

            {selectedItem.screenshot_url && (
              <div>
                <p className="block text-sm font-medium text-gray-700 mb-1">
                  Screenshot
                </p>
                <a href={selectedItem.screenshot_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={selectedItem.screenshot_url}
                    alt="Bug report screenshot"
                    className="w-full max-h-64 object-contain rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                  />
                </a>
              </div>
            )}

            <div>
              <p className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </p>
              <Select
                value={selectedItem.status}
                onChange={(e) => {
                  statusMutation.mutate({ id: selectedItem.id, status: e.target.value });
                  setSelectedItem({ ...selectedItem, status: e.target.value as FeedbackItem['status'] });
                }}
                options={statusOptions}
                aria-label="Feedback status"
              />
            </div>

            <div>
              <label htmlFor="feedback-admin-response" className="block text-sm font-medium text-gray-700 mb-1">
                Admin Response
              </label>
              <textarea
                id="feedback-admin-response"
                name="adminResponse"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
                rows={4}
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="Write your response to the user..."
                aria-label="Admin response"
              />
            </div>
          </div>
        )}

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setSelectedItem(null);
              setAdminResponse('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => responseMutation.mutate()}
            disabled={!adminResponse.trim() || responseMutation.isPending}
          >
            {responseMutation.isPending ? 'Saving...' : 'Save Response'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
